import { useRef, RefObject, useCallback, SyntheticEvent } from 'react';

import { SearchableVendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { setCookie, getCookie, deleteCookie } from '@zola-helpers/client/dist/es/util/storage';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import { ButtonV3Props } from '@zola/zola-ui/src/components/ButtonV3/ButtonV3';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';

import cx from 'classnames';

import { SaveSearchRequest } from '~/actions/couples/savedSearchActions';
import { useUserContext } from '~/contexts/UserContext';
import { useToast } from '~/hooks/useToast';
import { VendorSearchRequest } from '~/pages/couples/explore/components/SearchResults/types/types';
import { buildSearchRequest } from '~/pages/couples/explore/util/buildSearchRequest';
import type { OptionFacetType } from '~/types/types';
import { isSearchLocation } from '~/util/searchUtils';

import { useSaveNewSearchModal, useSavedSearchesListModal } from './showSavedSearchesModals';

import styles from './saveNewSearchButton.module.less';

type SaveNewSearchButtonProps = {
  vendorSearch: VendorSearchRequest;
  selectedFacetOptions: OptionFacetType[];
  availabilityDates?: string[] | null;
  cta?: React.ReactNode;
  className?: string;
} & (
  | ({
      component: 'button';
    } & Pick<ButtonV3Props, 'size' | 'variant'>)
  | {
      component: 'link';
    }
);

const VENDOR_SAVE_SEARCH_DATA_COOKIE = 'VENDOR_SAVE_SEARCH_DATA';
const SAVED_SEARCH_ONBOARDING_COMPLETED_COOKIE = 'SAVED_SEARCH_ONBOARDING_COMPLETED';

const SaveNewSearchButton = (props: SaveNewSearchButtonProps) => {
  const {
    vendorSearch,
    selectedFacetOptions,
    availabilityDates,
    cta = 'Save search',
    component,
    className,
  } = props;
  const { openSaveNewSearchModal } = useSaveNewSearchModal();
  const { openSavedSearchesListModal } = useSavedSearchesListModal();
  const { selectedTaxonomy, selectedLocation } = vendorSearch;
  const vendorTaxonomyKey = selectedTaxonomy?.key as SearchableVendorTaxonomyKey;
  const openModalCtaRef: RefObject<HTMLButtonElement> | null = useRef(null);
  const { is_guest: isGuest = true } = useUserContext() || {};
  const { negativeToast } = useToast();

  const showSavedSearchesModal = useCallback(() => {
    openSavedSearchesListModal({
      triggerRef: openModalCtaRef,
      vendorTaxonomyKey,
    });
  }, [openSavedSearchesListModal, vendorTaxonomyKey]);

  const showSaveModal = useCallback(() => {
    openSaveNewSearchModal({
      triggerRef: openModalCtaRef,
      vendorSearch,
      selectedFacetOptions,
      availabilityDates,
      openSavedSearchesListModal: showSavedSearchesModal,
    });
  }, [
    availabilityDates,
    openSaveNewSearchModal,
    selectedFacetOptions,
    showSavedSearchesModal,
    vendorSearch,
  ]);

  const onSaveSearch = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();

      if (!selectedTaxonomy?.key || !isSearchLocation(selectedLocation)) {
        negativeToast({
          headline: 'Please select a location and vendor',
        });
        return;
      }

      if (isGuest) {
        const v4StorefrontSearchRequest = buildSearchRequest(
          vendorSearch,
          60, // page size, Note: page size isn't persisted as part of the saved search
          false // do not allow unclaimed listings
        );
        const saveSearchPayload: SaveSearchRequest = {
          label: `${v4StorefrontSearchRequest.city}, ${v4StorefrontSearchRequest.stateProvince} - ${v4StorefrontSearchRequest.vendorType}`,
          v4StorefrontSearchRequest,
        };
        const onboardingData = {
          returnURL: window.location.href,
          saveSearchPayload,
        };
        setCookie(VENDOR_SAVE_SEARCH_DATA_COOKIE, JSON.stringify(onboardingData));
        window.location.assign('/wedding/onboard/vendor-saved-search');
      } else {
        showSaveModal();
      }
    },
    [isGuest, negativeToast, selectedLocation, selectedTaxonomy, showSaveModal, vendorSearch]
  );

  useEffectOnce(() => {
    if (getCookie(SAVED_SEARCH_ONBOARDING_COMPLETED_COOKIE)) {
      deleteCookie(SAVED_SEARCH_ONBOARDING_COMPLETED_COOKIE);
      if (!isGuest) {
        showSaveModal();
      }
    }
  });

  if (component === 'link') {
    return (
      <LinkV2
        innerRef={openModalCtaRef}
        className={className}
        role="button"
        onClick={onSaveSearch}
        noTextTransform
      >
        {cta}
      </LinkV2>
    );
  }

  const { size, variant } = props;

  return (
    <div className={cx(styles.saveSearchButton, className)}>
      <ButtonV3
        fullWidth
        onClick={onSaveSearch}
        innerRef={openModalCtaRef}
        size={size}
        variant={variant}
      >
        {cta}
      </ButtonV3>
    </div>
  );
};

export default SaveNewSearchButton;
